import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../../assets/api';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  TELEGRAM_CONTACT_CHANNEL,
} from '../../../assets/constants/verificationChannels';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import { registerWithPhone } from '../../../assets/services/auth';
import { extractContactFromResponse } from '../../../assets/utils/telegramContact';
import { PhoneIcon } from '../../../assets/icons/icons';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import './OnboardingSharePhone.css';

const TELEGRAM_CONTACT_CODE_REQUESTED_KEY = 'obank_telegram_contact_code_requested';
const TELEGRAM_CONTACT_REQUEST_TIMEOUT_MS = 60000;

function isTelegramContactCodeRequested() {
  return sessionStorage.getItem(TELEGRAM_CONTACT_CODE_REQUESTED_KEY) === 'true';
}

function markTelegramContactCodeRequested() {
  sessionStorage.setItem(TELEGRAM_CONTACT_CODE_REQUESTED_KEY, 'true');
}

function markCurrentUserPhoneVerified(currentUser, phoneNumber) {
  if (!currentUser) {
    return currentUser;
  }

  return {
    ...currentUser,
    phone: phoneNumber ?? currentUser.phone,
    reg_data: {
      ...currentUser.reg_data,
      stages: {
        ...currentUser.reg_data?.stages,
        is_phone_verified: true,
      },
    },
  };
}

function requestTelegramContact(webApp) {
  return new Promise((resolve) => {
    let didResolve = false;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      try {
        webApp.offEvent?.('contactRequested', handleContactRequested);
      } catch {
        // Telegram SDK availability differs by client/version.
      }
    };

    const finish = (accepted, response) => {
      if (didResolve) {
        return;
      }

      didResolve = true;
      cleanup();
      resolve({
        accepted: accepted === true || accepted === 'sent' || response?.status === 'sent',
        contact: extractContactFromResponse(accepted, response),
      });
    };

    function handleContactRequested(event = {}) {
      finish(event.status === 'sent', event);
    }

    try {
      webApp.onEvent?.('contactRequested', handleContactRequested);
    } catch {
      // Callback path below is enough on most Telegram clients.
    }

    timeoutId = window.setTimeout(() => finish(false), TELEGRAM_CONTACT_REQUEST_TIMEOUT_MS);

    try {
      const result = webApp.requestContact((accepted, response) => {
        finish(accepted, response);
      });

      if (result && typeof result.then === 'function') {
        result.then((accepted) => finish(accepted)).catch(() => finish(false));
      }
    } catch {
      finish(false);
      return;
    }
  });
}

function OnboardingSharePhone({ authService = authApi }) {
  const navigate = useNavigate();
  const { onboarding } = useContext(TranslationContext);
  const { refreshCurrentUser, setCurrentUser } = useContext(CurrentUserContext);
  const { webApp, initData, user: telegramUser } = useTelegram();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleShare = async () => {
    if (!webApp?.requestContact) {
      setError(onboarding.telegramOpenRequired);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    if (!isTelegramContactCodeRequested()) {
      try {
        await authService.requestPhoneCode({
          preferredChannel: TELEGRAM_CONTACT_CHANNEL,
        });
        markTelegramContactCodeRequested();
      } catch {
        setError(onboarding.phoneConfirmRequestFailed);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const { accepted, contact } = await requestTelegramContact(webApp);

      if (!accepted) {
        return;
      }

      await registerWithPhone({
        initData,
        phoneNumber: contact?.phone_number,
        telegramUserId: contact?.user_id ?? telegramUser?.id,
      });
      let confirmedPhone = contact?.phone_number;

      try {
        const refreshedUser = await refreshCurrentUser?.({ silent: true });
        confirmedPhone = confirmedPhone ?? refreshedUser?.phone;
      } catch {
        // If backend has not processed Telegram contact yet, keep the local stage update.
      }

      setCurrentUser((prevUser) => (
        markCurrentUserPhoneVerified(prevUser, confirmedPhone)
      ));

      navigate('/onboarding/passcode');
    } catch {
      setError(onboarding.registrationFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="onboarding-share-phone">
      <div className="onboarding-share-phone__content">
        <div className="onboarding-share-phone__illustration" aria-hidden="true">
          <div className="onboarding-share-phone__illustration-svg">
            <PhoneIcon className="onboarding-share-phone__phone-icon" />
          </div>
        </div>

        <h1 className="onboarding-share-phone__title">{onboarding.sharePhoneTitle}</h1>
        <p className="onboarding-share-phone__subtitle">{onboarding.sharePhoneText}</p>

        {error && (
          <p className="onboarding-share-phone__error" role="alert">
            {error}
          </p>
        )}

        <div className="onboarding-share-phone__badge">
          {onboarding.requestedSecurely}
        </div>
      </div>

      <BottomActionButton
        className="onboarding-share-phone__cta"
        onClick={handleShare}
        disabled={isSubmitting}
      >
        {isSubmitting ? onboarding.sharingPhone : onboarding.sharePhoneNumberAction}
      </BottomActionButton>
    </section>
  );
}

export default OnboardingSharePhone;
